import {Link} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/link.types';
import {
  GetGenderedCardsResponse_CardSliders_Card,
  GetGenderedCardsResponse_CardSliders_Card_CTA,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/browse_page/v1/service.types';
import {
  TrackingPromotionCreative,
  originClick,
} from '@aboutyou/legacy-tracking-sdk';
import {
  TrackPromotions,
  useTrackFeature,
  useTrackOriginClick,
} from '@aboutyou/tracking';
import {
  Button,
  COLORS,
  Card,
  CardContentContainer,
  FONT_SIZES,
  FONT_WEIGHTS,
  InfoCircleWithDialog,
  Text,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {useHistory} from 'react-router';
import {useIsAuthenticated} from '../../../features/Authentication/hooks/useIsAuthenticated';
import {Countdown} from '../Countdown/Countdown';

interface CardSlideProps extends GetGenderedCardsResponse_CardSliders_Card {
  className?: string;
  isCloseToViewport: boolean;
  ['data-testid']: string;
}

// eslint-disable-next-line complexity
export const CardSlide: React.FC<CardSlideProps> = ({
  headline,
  subline,
  image,
  cta,
  bannerTarget,
  type,
  className,
  tracker,
  isCloseToViewport,
  'data-testid': dataTestId,
}) => {
  const history = useHistory();
  const trackOriginClick = useTrackOriginClick();

  const {promotionId, promotionName, locationId, destination} = tracker;
  const {isAuthenticated} = useIsAuthenticated();
  const trackFeature = useTrackFeature();

  let categoryLinks: Link[] | undefined,
    cta1: GetGenderedCardsResponse_CardSliders_Card_CTA | undefined,
    cta2: GetGenderedCardsResponse_CardSliders_Card_CTA | undefined,
    endsAt: Date | undefined,
    infoTooltip: string | undefined;

  if (type?.$case === 'campaignCard') {
    ({categoryLinks, cta1, cta2, endsAt, infoTooltip} = type.campaignCard);
  }

  const onCardClick = () => {
    // tracking origin should be location ID, and since this is coming from backend it's not typed in the SDK, therefore is casted here to make typescript happy
    trackOriginClick(locationId as originClick)();
    trackFeature({
      eventType: 'click',
      label: `browse|bas|card_slider|cards|1|${isAuthenticated ? 1 : 0}`,
    });
  };

  return (
    <TrackPromotions
      promotion={{
        source: TrackingPromotionCreative.Newscard,
        id: promotionId,
        name: promotionName,
        locationId: locationId!,
        path: destination!,
      }}
      streamPosition={0}
      recoList={{
        sectionIndex: 0,
        recoType: locationId,
        elements: [
          {
            id: +promotionId,
            type: type?.$case === 'campaignCard' ? 'category' : 'story',
          },
        ],
      }}
      triggerOnce
      threshold={0.5}
    >
      <Card
        data-testid={dataTestId}
        className={className}
        onClick={onCardClick}
        isCloseToViewport={isCloseToViewport}
        image={image}
        href={bannerTarget.url}
      >
        {endsAt && (
          <CountdownContainer>
            <Countdown endsAt={endsAt} />
          </CountdownContainer>
        )}
        {!headline && infoTooltip && (
          <TopInfoCircleWithDialog conditions={infoTooltip} iconSize="SS" />
        )}
        <CardContentContainer>
          {subline && <Subline>{subline}</Subline>}
          {headline &&
            (infoTooltip ? (
              <HeadlineContainer>
                <Headline>{headline}</Headline>
                <HeadlineInfoCircleWithDialog
                  conditions={infoTooltip}
                  iconSize="SS"
                />
              </HeadlineContainer>
            ) : (
              <Headline>{headline}</Headline>
            ))}
          {categoryLinks && categoryLinks.length > 0 && (
            <CategoriesContainer data-testid="categoriesContainer">
              {categoryLinks.map(category => (
                <Category key={category.label} href={category.linkTarget.url}>
                  {category.label}
                </Category>
              ))}
            </CategoriesContainer>
          )}
          {(cta?.link.label || cta1?.link.label || cta2?.link.label) && (
            <CTAContainer>
              {cta?.link.label && (
                <CTA
                  href={
                    cta.link.linkTarget.url === bannerTarget.url
                      ? undefined
                      : cta.link.linkTarget.url
                  }
                  onClick={
                    cta.link.linkTarget.url === bannerTarget.url
                      ? () => history.push(cta.link.linkTarget.url)
                      : undefined
                  }
                  type="secondary"
                  data-testid="ctaLink"
                >
                  {cta.link.label}
                </CTA>
              )}
              {cta1?.link.label && (
                <CTA
                  href={
                    cta1.link.linkTarget.url === bannerTarget.url
                      ? undefined
                      : cta1.link.linkTarget.url
                  }
                  onClick={
                    cta1.link.linkTarget.url === bannerTarget.url
                      ? () => history.push(cta1!.link.linkTarget.url)
                      : undefined
                  }
                  type="secondary"
                  data-testid="ctaLink"
                >
                  {cta1.link.label}
                </CTA>
              )}
              {cta2?.link.label && (
                <CTA
                  href={
                    cta2.link.linkTarget.url === bannerTarget.url
                      ? undefined
                      : cta2.link.linkTarget.url
                  }
                  onClick={
                    cta2.link.linkTarget.url === bannerTarget.url
                      ? () => history.push(cta2!.link.linkTarget.url)
                      : undefined
                  }
                  type="secondary"
                  data-testid="ctaLink"
                >
                  {cta2.link.label}
                </CTA>
              )}
            </CTAContainer>
          )}
        </CardContentContainer>
      </Card>
    </TrackPromotions>
  );
};

const CountdownContainer = styled.div`
  position: absolute;
  top: 50px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
`;

const HeadlineContainer = styled.div`
  display: flex;
  position: relative;
`;

const Headline = styled(Text)`
  font-size: ${FONT_SIZES.px48};
  font-weight: ${FONT_WEIGHTS.heavy};
  line-height: ${FONT_SIZES.px54};
  color: ${COLORS.White};
`;

const HeadlineInfoCircleWithDialog = styled(InfoCircleWithDialog)`
  &&& {
    top: 0;
    right: -28px;
    pointer-events: all;
  }
`;

const TopInfoCircleWithDialog = styled(InfoCircleWithDialog)`
  &&& {
    top: 16px;
    right: 12px;
    z-index: 1;
  }
`;

const Subline = styled(Text)`
  margin-bottom: 8px;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: ${FONT_SIZES.px20};
  color: ${COLORS.White};
  text-transform: uppercase;
`;

const CategoriesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 28px;
  pointer-events: all;
`;

const Category = styled(Button)`
  & > span {
    width: 100%;
    min-width: 120px;
    height: 100%;
    padding: 11px 16px;
    border-radius: 3px;
    background-color: transparent;
    border: 1px solid ${COLORS.White};
    font-size: ${FONT_SIZES.px12};
    font-weight: ${FONT_WEIGHTS.mediumBold};
    line-height: ${FONT_SIZES.px18};
    color: ${COLORS.White};
    box-shadow: none;

    :hover {
      box-shadow: none;
    }
  }
`;

const CTAContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 28px;
  pointer-events: all;
`;

const CTA = styled(Button)`
  & > span {
    box-shadow: none;
  }
`;
